import { useCallback } from 'react';
import { DailyVideo, useLocalSessionId } from '@daily-co/daily-react';
import { useSubjectLanguageVariants } from '@features/user';
import { PipIcon } from '@jotpsych/ui/icons/PipIcon';
import {
  ArrowBack as ArrowBackIcon,
  ChatBubbleOutlined as ChatIcon,
  Fullscreen as FullscreenIcon,
  Logout as LogoutIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
} from '@mui/icons-material';
import { Badge, Box, IconButton, Stack, Typography } from '@mui/material';
import type { DailyCallService } from '@services/dailycall';
import { useDailyCallState } from '@services/dailycall';
import { useAppSelector } from '@store/hooks';

import { useCallLayout } from '../../hooks/useCallLayout';
import { selectChatUnreadCount } from '../../store';
import { ParticipantTile } from '../ParticipantTile';
import { RecordingStatusBar } from '../RecordingStatusBar';

interface CompactPipViewProps {
  dailyCallService: DailyCallService;
  isRecording: boolean;
  isRecordingStarting?: boolean;
  isRecordingPaused?: boolean;
  onLeave: () => void;
  onClose?: () => void;
  onEnterPip?: () => void;
  isPipSupported?: boolean;
  onOpenChat?: () => void;
  onPauseRecording?: () => void;
  onResumeRecording?: () => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
}

export function CompactPipView({
  dailyCallService,
  isRecording,
  isRecordingStarting,
  isRecordingPaused = false,
  onLeave,
  onClose,
  onEnterPip,
  isPipSupported = false,
  onOpenChat,
  onPauseRecording,
  onResumeRecording,
  onToggleFullscreen,
  isFullscreen: _isFullscreen,
}: CompactPipViewProps) {
  const callState = useDailyCallState(dailyCallService);
  const layout = useCallLayout();
  const localSessionId = useLocalSessionId();
  const { singular } = useSubjectLanguageVariants();
  const chatUnreadCount = useAppSelector(selectChatUnreadCount);

  // Main-stage participant: remote screen share takes precedence over the active speaker.
  const mainStageId = layout.hasRemoteScreenShare
    ? layout.screenShareSessionId
    : layout.activeSpeakerId;
  const hasRemoteParticipants = mainStageId !== null;

  const handleToggleMic = useCallback(() => {
    dailyCallService.setLocalAudio(!callState.localAudioEnabled);
  }, [callState.localAudioEnabled, dailyCallService]);

  const handleToggleVideo = useCallback(() => {
    dailyCallService.setLocalVideo(!callState.localVideoEnabled);
  }, [callState.localVideoEnabled, dailyCallService]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        bgcolor: 'grey.900',
        overflow: 'hidden',
      }}
    >
      {/* Recording status bar (top) */}
      {(isRecording || isRecordingStarting) && onPauseRecording && onResumeRecording && (
        <RecordingStatusBar
          isRecording={isRecording}
          isStarting={isRecordingStarting}
          isPaused={isRecordingPaused}
          onPause={onPauseRecording}
          onResume={onResumeRecording}
          hasRemoteParticipant={hasRemoteParticipants}
          compact
        />
      )}

      {/* Header: close + PiP controls */}
      <Box
        sx={{
          position: 'absolute',
          top: isRecording ? 36 : 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1,
          py: 0.5,
          zIndex: 2,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)',
        }}
      >
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          {onClose && (
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ color: 'common.white', p: 0.5 }}
              aria-label="Return to encounter"
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Stack>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          {chatUnreadCount > 0 && onOpenChat && (
            <IconButton
              onClick={onOpenChat}
              size="small"
              sx={{ color: 'common.white', p: 0.5 }}
              aria-label="Open chat"
            >
              <Badge badgeContent={chatUnreadCount} color="error" max={99}>
                <ChatIcon sx={{ fontSize: 18 }} />
              </Badge>
            </IconButton>
          )}
          {isPipSupported && onEnterPip && (
            <IconButton
              onClick={onEnterPip}
              size="small"
              sx={{ color: 'common.white', p: 0.5 }}
              aria-label="Picture-in-picture"
            >
              <PipIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Video area */}
      <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
        {mainStageId ? (
          <ParticipantTile
            sessionId={mainStageId}
            variant="main"
            isScreenShare={layout.hasRemoteScreenShare}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography variant="body2" sx={{ color: 'grey.400' }}>
              {`Waiting for ${singular}...`}
            </Typography>
          </Box>
        )}

        {/* Local self-view thumbnail */}
        {localSessionId && callState.localVideoEnabled && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              width: 80,
              height: 60,
              borderRadius: 0.5,
              border: '1px solid',
              borderColor: 'common.white',
              overflow: 'hidden',
              bgcolor: 'grey.800',
            }}
          >
            <DailyVideo
              sessionId={localSessionId}
              type="video"
              mirror
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        )}
      </Box>

      {/* Controls bar */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          py: 0.75,
          px: 1,
          bgcolor: 'rgba(0,0,0,0.8)',
        }}
      >
        <IconButton
          onClick={handleToggleMic}
          size="small"
          sx={{ color: callState.localAudioEnabled ? 'common.white' : 'error.main' }}
          aria-label={callState.localAudioEnabled ? 'Mute microphone' : 'Unmute microphone'}
        >
          {callState.localAudioEnabled ? (
            <MicIcon sx={{ fontSize: 20 }} />
          ) : (
            <MicOffIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>

        <IconButton
          onClick={handleToggleVideo}
          size="small"
          sx={{ color: callState.localVideoEnabled ? 'common.white' : 'error.main' }}
          aria-label={callState.localVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
        >
          {callState.localVideoEnabled ? (
            <VideocamIcon sx={{ fontSize: 20 }} />
          ) : (
            <VideocamOffIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>

        <IconButton
          onClick={onLeave}
          size="small"
          sx={{ color: 'error.main' }}
          aria-label="Leave call"
        >
          <LogoutIcon sx={{ fontSize: 20 }} />
        </IconButton>

        {onToggleFullscreen && (
          <IconButton
            onClick={onToggleFullscreen}
            size="small"
            sx={{ color: 'common.white' }}
            aria-label="Toggle fullscreen"
          >
            <FullscreenIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Stack>
    </Box>
  );
}
